<template>
  <div class="ac-quality">
    <div class="ac-head">
      <h1>🤖 Auto-Confirm Quality</h1>
      <p class="sub">What the AI decided vs. what really happened — so you can trust it before going live.</p>
      <button class="refresh" @click="load" :disabled="loading">{{ loading ? 'Loading…' : '↻ Refresh' }}</button>
    </div>

    <!-- full-page spinner only on the FIRST load (no data yet) -->
    <div v-if="isAdmin !== true || (loading && !data)" class="loading">
      <span class="spinner big"></span> Loading…
    </div>

    <div v-else>
      <!-- ═══ REAL-DATA QUALITY (backtest on historical orders) ═══ -->
      <div v-if="bt" class="realbox">
        <div class="realhead collapsible" @click="showRealQuality = !showRealQuality">
          <h2>
            <span class="caret">{{ showRealQuality ? '▼' : '▶' }}</span>
            📈 Real Quality — tested on {{ bt.totalOrders.toLocaleString() }} historical orders
            <span class="muted sm" v-if="!showRealQuality">— {{ bt.recommended.precision }}% precision (click to open)</span>
          </h2>
          <p class="sub" v-if="showRealQuality">This is the AI's true accuracy on real past orders (delivered vs failed). Baseline (confirm everyone): <b>{{ bt.baselineDeliverRate }}%</b>.</p>
        </div>
        <div v-show="showRealQuality">
        <div class="cards">
          <div class="card">
            <div class="big">{{ bt.recommended.precision }}%</div>
            <div class="lbl">Precision @ {{ bt.recommended.threshold }}%</div>
            <div class="note">of auto-confirms that reached Processed (cash collected)</div>
          </div>
          <div class="card">
            <div class="big">{{ bt.recommended.autoConfirm.toLocaleString() }}</div>
            <div class="lbl">Would auto-confirm</div>
            <div class="note">calls saved at this bar</div>
          </div>
          <div class="card warn">
            <div class="big">{{ bt.recommended.mistakes.toLocaleString() }}</div>
            <div class="lbl">Mistakes</div>
            <div class="note">auto-confirmed but failed (refused / returned)</div>
          </div>
          <div class="card">
            <div class="big">+{{ (bt.recommended.precision - bt.baselineDeliverRate).toFixed(1) }}pt</div>
            <div class="lbl">Better than baseline</div>
            <div class="note">vs confirming blindly</div>
          </div>
        </div>

        <div class="cols">
          <div>
            <h3>Pick your threshold (real data)</h3>
            <table class="thr">
              <thead><tr><th>Bar</th><th>Auto-confirm</th><th>Orders</th><th>Precision</th></tr></thead>
              <tbody>
                <tr v-for="t in bt.thresholds" :key="t.threshold">
                  <td>≥ {{ t.threshold }}%</td>
                  <td>{{ t.coverage }}%</td>
                  <td>{{ t.count.toLocaleString() }}</td>
                  <td :class="precClass(t.precision)">{{ t.precision != null ? t.precision + '%' : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <h3>By country (@ 85%)</h3>
            <table class="thr">
              <thead><tr><th>Country</th><th>Auto-confirm</th><th>Precision</th><th>Orders</th></tr></thead>
              <tbody>
                <tr v-for="c in bt.byCountry" :key="c.country">
                  <td>{{ c.country }}</td>
                  <td>{{ c.coverage }}%</td>
                  <td :class="precClass(c.precision)">{{ c.precision }}%</td>
                  <td>{{ c.n.toLocaleString() }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        </div><!-- /v-show showRealQuality -->
      </div>
      <div v-else class="realbox muted-box">
        Real-data quality not available yet — run the backtest to populate it.
      </div>

      <hr class="divide" />
      <h2 class="shadow-title">🕶️ Live shadow decisions (from this app)</h2>
      <p class="sub">Decisions the AI logged on orders created here vs. their real status. (Small sample — the real proof is above.)</p>
    </div>

    <div v-if="data && isAdmin === true">
      <!-- SCORECARD -->
      <div class="cards">
        <div class="card">
          <div class="big">{{ sc.aiPrecision != null ? sc.aiPrecision + '%' : '—' }}</div>
          <div class="lbl">AI precision</div>
          <div class="note">of auto-confirms that reached Processed (cash collected)</div>
        </div>
        <div class="card">
          <div class="big">{{ sc.callsSaved }}</div>
          <div class="lbl">Calls saved</div>
          <div class="note">orders the AI auto-confirmed (resolved)</div>
        </div>
        <div class="card warn">
          <div class="big">{{ sc.aiWrong }}</div>
          <div class="lbl">Mistakes</div>
          <div class="note">auto-confirmed but failed (refused / returned)</div>
        </div>
        <div class="card">
          <div class="big">{{ sc.baselineDeliverRate != null ? sc.baselineDeliverRate + '%' : '—' }}</div>
          <div class="lbl">Baseline</div>
          <div class="note">overall delivery rate (all orders)</div>
        </div>
        <div class="card">
          <div class="big">{{ sc.missedGood }}</div>
          <div class="lbl">Missed good</div>
          <div class="note">sent to call but would've delivered</div>
        </div>
      </div>

      <p class="verdict" v-if="sc.aiPrecision != null">
        <template v-if="sc.resolved >= 100 && sc.aiPrecision >= sc.baselineDeliverRate + 10">
          ✅ The AI is clearly better than confirming blindly
          (<b>{{ sc.aiPrecision }}%</b> vs baseline {{ sc.baselineDeliverRate }}%).
        </template>
        <template v-else>
          ⏳ Still collecting live outcomes. Only <b>{{ sc.resolved }}</b> of {{ sc.totalDecisions }}
          orders have reached a final state (Processed = cash collected, or failed),
          which takes days–weeks per order. The trustworthy number is the
          <b>Real Quality</b> section above ({{ (bt && bt.recommended && bt.recommended.precision) || '90.7' }}%
          on {{ (bt && bt.totalOrders && bt.totalOrders.toLocaleString()) || '46,034' }} completed orders).
        </template>
      </p>

      <!-- THRESHOLD ANALYSIS (live shadow — collapsed by default) -->
      <div class="section">
        <h2 class="collapsible" @click="showLiveThresholds = !showLiveThresholds">
          <span class="caret">{{ showLiveThresholds ? '▼' : '▶' }}</span>
          Pick your threshold (live)
          <span class="muted sm">— from this app's shadow decisions (click to {{ showLiveThresholds ? 'hide' : 'show' }})</span>
        </h2>
        <div v-if="showLiveThresholds">
          <p class="sub">On your real orders: at each confidence bar, how many you'd auto-confirm and how accurate. (Fills in as orders complete delivery.)</p>
          <table class="thr">
            <thead><tr><th>Confidence bar</th><th>Auto-confirm %</th><th>Orders</th><th>Precision</th></tr></thead>
            <tbody>
              <tr v-for="t in data.thresholds" :key="t.threshold">
                <td>≥ {{ t.threshold }}%</td>
                <td>{{ t.coverage }}%</td>
                <td>{{ t.count }}</td>
                <td :class="precClass(t.precision)">{{ t.precision != null ? t.precision + '%' : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TABLE -->
      <div class="section">
        <h2>Order-by-order</h2>

        <!-- ═══ All filters in one card ═══ -->
        <div class="filter-card">
          <!-- row 1: decision pills -->
          <div class="frow">
            <span class="frow-label">Decision</span>
            <div class="pills">
              <button v-for="f in ['all','AUTO_CONFIRM','MANUAL','delivered','failed']" :key="f"
                      :class="{active: filter===f}" @click="filter=f">{{ label(f) }}</button>
            </div>
          </div>

          <!-- row 2: verdict pills (AI vs final DELIVERY) -->
          <div class="frow">
            <span class="frow-label">Delivery</span>
            <div class="pills">
              <button v-for="v in verdictFilters" :key="v.key"
                      :class="['vpill', v.cls, { active: fVerdict === v.key }]"
                      @click="fVerdict = v.key">{{ v.label }}</button>
            </div>
          </div>

          <!-- row 2b: agreement with call-center status (fast, same-day) -->
          <div class="frow">
            <span class="frow-label">Confirm</span>
            <div class="pills">
              <button v-for="v in agreeFilters" :key="v.key"
                      :class="['vpill', v.cls, { active: fAgree === v.key }]"
                      @click="fAgree = v.key">{{ v.label }}</button>
            </div>
          </div>

          <!-- row 3: search + dropdowns + confidence -->
          <div class="frow wrap">
            <input class="xsearch" type="text" v-model="search"
                   placeholder="🔍 Search order, phone, or customer…" />
            <!-- custom Model dropdown -->
            <div class="dd" :class="{open: ddOpen==='model'}">
              <button class="dd-btn" @click="toggleDd('model')">
                <span>{{ modelLabel }}</span><span class="dd-caret">▾</span>
              </button>
              <div class="dd-menu" v-if="ddOpen==='model'">
                <div v-for="m in modelOptions" :key="m.value"
                     class="dd-item" :class="{sel: fModel===m.value}"
                     @click="fModel=m.value; ddOpen=null">
                  <span class="dd-dot" :style="{background:m.color}"></span>{{ m.label }}
                </div>
              </div>
            </div>

            <!-- custom Status dropdown -->
            <div class="dd" :class="{open: ddOpen==='status'}">
              <button class="dd-btn" @click="toggleDd('status')">
                <span>{{ fStatus || 'Any real status' }}</span><span class="dd-caret">▾</span>
              </button>
              <div class="dd-menu" v-if="ddOpen==='status'">
                <div class="dd-item" :class="{sel: fStatus===''}" @click="fStatus=''; ddOpen=null">Any real status</div>
                <div v-for="s in statusOptions" :key="s"
                     class="dd-item" :class="{sel: fStatus===s}" @click="fStatus=s; ddOpen=null">{{ s }}</div>
              </div>
            </div>
            <div class="conf-range">
              <span class="clbl">Confidence</span>
              <div class="conf-inputs">
                <input type="number" min="0" max="100" v-model.number="confMin" class="cnum" />
                <span class="conf-sep">to</span>
                <input type="number" min="0" max="100" v-model.number="confMax" class="cnum" />
                <span class="conf-pct">%</span>
              </div>
            </div>
          </div>

          <!-- row 4: date range -->
          <div class="frow wrap">
            <div class="quick-chips">
              <button v-for="q in quickRanges" :key="q.days"
                      :class="{on: activeQuick === q.days}"
                      @click="quickRange(q.days)">{{ q.label }}</button>
            </div>
            <div class="date-fields">
              <date-picker
                v-model="dateRange"
                range
                format="YYYY-MM-DD"
                value-type="format"
                placeholder="Select date range…"
                :disabled-date="disabledAfterToday"
                @change="onRangeChange"
                class="ac-datepicker" />
              <span v-if="loading" class="inline-loader"><span class="spinner"></span> loading…</span>
            </div>
          </div>

          <!-- footer: active summary + reset all -->
          <div class="fcard-foot">
            <span class="muted sm count-line">
              <b>{{ filtered.length.toLocaleString() }}</b>
              order<template v-if="filtered.length !== 1">s</template>
              <template v-if="hasClientFilter"> match your filters</template>
              <template v-else-if="serverTotal !== null"> in this date range</template>
              <template v-if="hasClientFilter && loadedCount"> <span class="dim">(of {{ loadedCount.toLocaleString() }} in this date/decision range)</span></template>
            </span>
            <span v-if="fromDate || toDate" class="date-active">📅 {{ fromDate || 'start' }} → {{ toDate || 'today' }}</span>
            <button class="reset-all" v-if="anyFilterActive" @click="resetAllFilters">✕ Reset all filters</button>
          </div>
          <div v-if="isTruncated" class="trunc-warn">
            ⚠️ {{ serverTotal.toLocaleString() }} orders match this date range, but only {{ loadedCount.toLocaleString() }}
            are loaded. Narrow the date range to see the rest.
          </div>
        </div>
        <div class="table-wrap rows-card">
          <div v-if="loading && data" class="table-overlay"><span class="spinner"></span> Updating…</div>
        <table class="rows">
          <thead>
            <tr>
              <th class="c-date">Date</th>
              <th class="c-order">Order</th>
              <th class="c-phone">Phone</th>
              <th class="c-ai">AI said</th>
              <th class="c-conf">Conf.</th>
              <th class="c-why">Why</th>
              <th class="c-status">Real status</th>
              <th class="c-verdict">Verdict</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in paged" :key="r._id">
              <td class="c-date"><span class="cell-date">{{ fmtDate(r.createdAt) }}</span></td>
              <td class="c-order"><span class="order-ref">{{ r.orderRef || '—' }}</span><div class="muted sm cust-name">{{ r.customer }}</div></td>
              <td class="c-phone"><span class="phone-num">{{ r.phone }}</span></td>
              <td class="c-ai"><span :class="'pill ' + (r.decision==='AUTO_CONFIRM'?'ok':'warn')">{{ r.decision==='AUTO_CONFIRM'?'Confirm':'Call' }}</span></td>
              <td class="c-conf">
                <span class="conf-val" :class="confClass(r.confidence, r.model)">{{ r.confidence }}%</span>
                <span class="model-tag">{{ r.model }}</span>
              </td>
              <td class="why c-why">
                <div v-for="(p,i) in r.positives.slice(0,2)" :key="'p'+i" class="pos">✓ {{ p }}</div>
                <div v-for="(n,i) in r.negatives.slice(0,2)" :key="'n'+i" class="neg">! {{ n }}</div>
                <div v-if="!r.positives.length && !r.negatives.length" class="muted sm plain-reason">{{ (r.reasons[0]||'') }}</div>
              </td>
              <td class="c-status">
                <span class="status-badge" :class="statusClass(r.realStatus)">{{ r.realStatus }}</span>
                <div class="muted sm ship-line" v-if="r.shipStatus">ship: {{ r.shipStatus }}</div>
              </td>
              <td class="c-verdict">
                <span v-if="r.outcome==='pending'" class="pill muted-pill">pending</span>
                <span v-else-if="verdict(r)==='right'" class="pill ok">✓ AI right</span>
                <span v-else class="pill bad">✗ AI wrong</span>
              </td>
            </tr>
          </tbody>
        </table>
        </div><!-- /table-wrap -->
        <p v-if="!filtered.length && !loading" class="empty-rows">No orders match these filters.</p>

        <!-- PAGINATION BAR -->
        <div v-if="filtered.length" class="pagination">
          <div class="pg-info">
            Showing <b>{{ rangeStart }}–{{ rangeEnd }}</b> of <b>{{ filtered.length.toLocaleString() }}</b>
            <span v-if="serverTotal !== null && serverTotal > filtered.length" class="muted"> (of {{ serverTotal.toLocaleString() }} in range)</span>
          </div>
          <div class="pg-controls" v-if="totalPages > 1">
            <button class="pg-btn" :disabled="page === 1" @click="page = 1" title="First">«</button>
            <button class="pg-btn" :disabled="page === 1" @click="page--" title="Previous">‹</button>
            <button v-for="p in pageWindow" :key="p"
                    class="pg-num" :class="{ on: p === page }"
                    @click="page = p">{{ p }}</button>
            <button class="pg-btn" :disabled="page === totalPages" @click="page++" title="Next">›</button>
            <button class="pg-btn" :disabled="page === totalPages" @click="page = totalPages" title="Last">»</button>
          </div>
          <div class="pg-size">
            <span class="muted sm">Rows:</span>
            <button v-for="n in [10,25,50,100]" :key="n"
                    class="pg-size-btn" :class="{ on: perPage === n }"
                    @click="perPage = n">{{ n }}</button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="loading">No data. Create some orders first (shadow mode logs them here).</div>
  </div>
</template>

<script>
const ADMIN_TYPES = ['Admin', 'Administrateur', 'CEO'];

export default {
  name: 'AutoConfirmQuality',
  data() {
    return {
      loading: false, data: null, bt: null, filter: 'all', isAdmin: null,
      page: 1, perPage: 25, fromDate: '', toDate: '', dateRange: [], showLiveThresholds: false, showRealQuality: false,
      search: '', fModel: '', fStatus: '', confMin: 0, confMax: 100, fVerdict: 'all',
      ddOpen: null,
      modelOptions: [
        { value: '', label: 'All models', color: '#9ca3af' },
        { value: 'ml', label: 'AI model (ml)', color: '#2563eb' },
        { value: 'rule', label: 'Rule', color: '#f59e0b' },
        { value: 'guard', label: 'Guard (junk/duplicate)', color: '#dc2626' },
      ],
      verdictFilters: [
        { key: 'all', label: 'All', cls: '' },
        { key: 'right', label: '✓ AI right (delivery)', cls: 'v-right' },
        { key: 'wrong', label: '✗ AI wrong (delivery)', cls: 'v-wrong' },
        { key: 'confirm_delivered', label: 'Confirm → Delivered (Processed)', cls: 'v-right' },
        { key: 'confirm_failed', label: 'Confirm → Failed', cls: 'v-wrong' },
        { key: 'call_failed', label: 'Call → Failed', cls: 'v-right' },
        { key: 'call_delivered', label: 'Call → Delivered (Processed)', cls: 'v-wrong' },
        { key: 'pending', label: 'Not resolved yet', cls: 'v-pending' },
      ],
      // agreement vs the call-center's own status (fast signal, same-day)
      agreeFilters: [
        { key: 'all', label: 'All', cls: '' },
        { key: 'confirm_confirmed', label: 'AI Confirm = Confirmed ✓', cls: 'v-right' },
        { key: 'confirm_cancelled', label: 'AI Confirm but Cancelled', cls: 'v-wrong' },
        { key: 'call_confirmed', label: 'AI Call but Confirmed', cls: 'v-pending' },
        { key: 'call_cancelled', label: 'AI Call = Cancelled ✓', cls: 'v-right' },
      ],
      fAgree: 'all',
      activeQuick: null,
      quickRanges: [
        { label: 'Today', days: 0 },
        { label: 'Yesterday', days: 1 },
        { label: 'Last 7 days', days: 7 },
        { label: 'Last 30 days', days: 30 },
        { label: 'Last 90 days', days: 90 },
      ],
    };
  },
  computed: {
    sc() { return (this.data && this.data.scorecard) || {}; },
    statusOptions() {
      if (!this.data) return [];
      return [...new Set(this.data.rows.map(r => r.realStatus).filter(Boolean))].sort();
    },
    anyXFilter() {
      return this.search || this.fModel || this.fStatus || this.confMin > 0 || this.confMax < 100;
    },
    anyFilterActive() {
      return this.anyXFilter || this.filter !== 'all' || this.fVerdict !== 'all' || this.fAgree !== 'all' || this.fromDate || this.toDate;
    },
    filtered() {
      if (!this.data) return [];
      const f = this.filter;
      const q = (this.search || '').toLowerCase().trim();
      return this.data.rows.filter(r => {
        // decision / outcome pill
        if (f === 'AUTO_CONFIRM' || f === 'MANUAL') { if (r.decision !== f) return false; }
        else if (f === 'delivered' || f === 'failed') { if (r.outcome !== f) return false; }
        // model
        if (this.fModel && r.model !== this.fModel) return false;
        // real status
        if (this.fStatus && r.realStatus !== this.fStatus) return false;
        // confidence range — coerce empty/NaN inputs to safe 0/100 bounds so
        // clearing a box doesn't silently invert the filter to zero results.
        const c = r.confidence || 0;
        const lo = Number.isFinite(+this.confMin) ? +this.confMin : 0;
        const hi = Number.isFinite(+this.confMax) && this.confMax !== '' ? +this.confMax : 100;
        if (c < lo || c > hi) return false;
        // free-text search across order ref / phone / customer
        if (q) {
          const hay = `${r.orderRef || ''} ${r.phone || ''} ${r.customer || ''}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        // verdict (AI-said × real-result combinations)
        if (this.fVerdict !== 'all' && !this.matchVerdict(r, this.fVerdict)) return false;
        // agreement with call-center status
        if (this.fAgree !== 'all' && !this.matchAgree(r, this.fAgree)) return false;
        return true;
      });
    },
    totalPages() { return Math.max(1, Math.ceil(this.filtered.length / this.perPage)); },
    paged() {
      const start = (this.page - 1) * this.perPage;
      return this.filtered.slice(start, start + this.perPage);
    },
    rangeStart() { return this.filtered.length ? (this.page - 1) * this.perPage + 1 : 0; },
    rangeEnd() { return Math.min(this.page * this.perPage, this.filtered.length); },
    // a window of up to 5 page numbers centered on the current page
    pageWindow() {
      const total = this.totalPages, cur = this.page, span = 5;
      let start = Math.max(1, cur - Math.floor(span / 2));
      let end = Math.min(total, start + span - 1);
      start = Math.max(1, end - span + 1);
      const out = [];
      for (let p = start; p <= end; p++) out.push(p);
      return out;
    },
    todayStr() { return new Date().toISOString().slice(0, 10); },
    modelLabel() { const m = this.modelOptions.find(o => o.value === this.fModel); return m ? m.label : 'All models'; },
    // server returned fewer rows than the true total for this date+decision+model
    // query → some matching orders aren't loaded for client-side refinement.
    serverTotal() { return this.data && typeof this.data.total === 'number' ? this.data.total : null; },
    loadedCount() { return this.data ? (this.data.rows || []).length : 0; },
    // client-only refinements active? (these narrow the loaded set further —
    // decision/model/date are server-side and already reflected in loadedCount)
    hasClientFilter() {
      return this.fVerdict !== 'all' || this.fAgree !== 'all' || !!this.fStatus ||
        !!this.search || this.confMin > 0 || this.confMax < 100 ||
        this.filter === 'delivered' || this.filter === 'failed';
    },
    isTruncated() {
      // real truncation = the server had MORE matches than it could send back
      return this.serverTotal !== null && this.serverTotal > this.loadedCount;
    },
  },
  watch: {
    // filter (decision) + model are SERVER-side → reload full dataset.
    filter() { this.page = 1; this.load(); },
    fModel() { this.page = 1; this.load(); },
    // the rest are client-side refinements → just reset the page.
    perPage() { this.page = 1; },
    search() { this.page = 1; },
    fStatus() { this.page = 1; },
    confMin() { this.page = 1; },
    confMax() { this.page = 1; },
    fVerdict() { this.page = 1; },
    fAgree() { this.page = 1; },
  },
  methods: {
    async checkAdmin() {
      try {
        const me = await this.$server.me('users');
        const user = me && me.content ? me.content : me;
        this.isAdmin = !!(user && user.type && ADMIN_TYPES.indexOf(user.type) !== -1);
      } catch (e) {
        this.isAdmin = false;
      }
      return this.isAdmin;
    },
    async load() {
      this.loading = true;
      // real-data backtest (the true quality) + live shadow logs, in parallel
      try {
        const btRaw = await this.$server.action('autoconfirm', 'backtest', {});
        const bt = btRaw && btRaw.content ? btRaw.content : btRaw;
        this.bt = bt && bt.thresholds ? bt : null;
      } catch (e) { this.bt = null; }
      try {
        // Push the coarse filters (date, decision, model) to the SERVER so they
        // filter the whole dataset — otherwise "Last 90 days" only sees the newest
        // page. Verdict/agree/status/confidence/search still refine client-side.
        const payload = { limit: 5000 };
        if (this.fromDate) payload.from = this.fromDate;
        if (this.toDate) payload.to = this.toDate;
        if (this.filter === 'AUTO_CONFIRM' || this.filter === 'MANUAL') payload.decision = this.filter;
        if (this.fModel) payload.model = this.fModel;
        const raw = await this.$server.action('autoconfirm', 'quality', payload);
        this.data = raw && raw.content ? raw.content : raw;
        this.page = 1;
      } catch (e) {
        console.log('quality load error', e && e.message);
      }
      this.loading = false;
    },
    toggleDd(which) { this.ddOpen = this.ddOpen === which ? null : which; },
    resetXFilters() { this.search = ''; this.fModel = ''; this.fStatus = ''; this.confMin = 0; this.confMax = 100; },
    resetAllFilters() {
      this.resetXFilters();
      this.filter = 'all';
      this.fVerdict = 'all';
      this.fAgree = 'all';
      this.fromDate = ''; this.toDate = ''; this.dateRange = []; this.activeQuick = null;
      this.load();
    },
    clearDates() { this.fromDate = ''; this.toDate = ''; this.dateRange = []; this.activeQuick = null; this.load(); },
    disabledAfterToday(date) { return date > new Date(); },
    // Format a Date as a LOCAL calendar day (YYYY-MM-DD) — never toISOString(),
    // which converts to UTC and can shift the day for UTC-positive timezones.
    localDay(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    // The picker emits YYYY-MM-DD STRINGS (value-type="format") — no TZ shift.
    onRangeChange(val) {
      if (Array.isArray(val) && val[0] && val[1]) {
        this.fromDate = val[0]; this.toDate = val[1];
      } else { this.fromDate = ''; this.toDate = ''; }
      this.activeQuick = null;
      this.load();
    },
    quickRange(days) {
      let from, to;
      if (days === 1) { // Yesterday = that single day
        const y = new Date(); y.setDate(y.getDate() - 1); from = y; to = y;
      } else {
        to = new Date();
        from = new Date(); from.setDate(from.getDate() - days);
      }
      const fs = this.localDay(from), ts = this.localDay(to);
      this.dateRange = [fs, ts];   // strings, matching value-type="format"
      this.fromDate = fs;
      this.toDate = ts;
      this.activeQuick = days;
      this.load();
    },
    fmtDate(v) {
      if (!v) return '—';
      const d = new Date(v);
      return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    verdict(r) {
      const good = r.decision === 'AUTO_CONFIRM';
      // AI "right" = it confirmed and it delivered, OR it said call and it failed
      if (good) return r.outcome === 'delivered' ? 'right' : 'wrong';
      return r.outcome === 'failed' ? 'right' : 'wrong';
    },
    // Match a row to one of the verdict / combination filters.
    matchVerdict(r, key) {
      const confirm = r.decision === 'AUTO_CONFIRM';
      const oc = r.outcome; // delivered | failed | pending
      if (key === 'pending') return oc === 'pending';
      if (oc === 'pending') return false; // not-yet-tested can't match a verdict
      const v = this.verdict(r); // 'right' | 'wrong'
      switch (key) {
        case 'right': return v === 'right';
        case 'wrong': return v === 'wrong';
        case 'confirm_delivered': return confirm && oc === 'delivered';
        case 'confirm_failed': return confirm && oc === 'failed';
        case 'call_failed': return !confirm && oc === 'failed';
        case 'call_delivered': return !confirm && oc === 'delivered';
        default: return true;
      }
    },
    // Match a row against agreement with the call-center's own status
    // (Confirmed / Cancelled). Fast same-day signal, unlike final delivery.
    matchAgree(r, key) {
      // Guard rows (junk phone / duplicate) are pre-scoring safety rejects, not
      // genuine AI "calls" — exclude them from agreement stats (matches the
      // backend scorecard which also excludes model==='guard').
      if (r.model === 'guard') return false;
      const confirm = r.decision === 'AUTO_CONFIRM';
      const st = r.realStatus;
      switch (key) {
        case 'confirm_confirmed': return confirm && st === 'Confirmed';
        case 'confirm_cancelled': return confirm && st === 'Cancelled';
        case 'call_confirmed': return !confirm && st === 'Confirmed';
        case 'call_cancelled': return !confirm && st === 'Cancelled';
        default: return true;
      }
    },
    precClass(p) { return p == null ? '' : (p >= 85 ? 'good' : p >= 70 ? 'mid' : 'low'); },
    // color-code the per-order confidence (>=threshold green, near amber, low red).
    // guard/rule rows (no real ml score) stay neutral.
    confClass(p, model) {
      if (model !== 'ml' || p == null) return 'neutral';
      return p >= 85 ? 'good' : p >= 70 ? 'mid' : 'low';
    },
    // color-code the real order status
    statusClass(st) {
      const s = (st || '').toLowerCase();
      if (['confirmed', 'delivered', 'paid', 'processed', 'received'].includes(s)) return 'st-green';
      if (['cancelled', 'refused', 'return', 'spam', 'double', 'rembourser', 'refunded', 'outofstock'].includes(s)) return 'st-red';
      if (['pending', 'unreached', 'toremind'].includes(s)) return 'st-gray';
      if (['shipped', 'a transférer', 'awaiting preparation', 'prepared', 'to prepare'].includes(s)) return 'st-blue';
      return 'st-gray';
    },
    label(f) {
      return { all: 'All', AUTO_CONFIRM: 'Auto-confirmed', MANUAL: 'Sent to call', delivered: 'Delivered (Processed)', failed: 'Failed' }[f] || f;
    },
  },
  async mounted() {
    // close custom dropdowns when clicking outside them
    this._closeDd = (e) => { if (!e.target.closest('.dd')) this.ddOpen = null; };
    document.addEventListener('click', this._closeDd);
    const ok = await this.checkAdmin();
    if (ok) this.load();
    else window.location.href = '/'; // non-admins: silent redirect to dashboard
  },
  beforeDestroy() {
    if (this._closeDd) document.removeEventListener('click', this._closeDd);
  },
};
</script>

<style scoped>
.ac-quality { padding: 24px 32px; width: 100%; color: #1f2937; box-sizing: border-box; }
.ac-head h1 { font-size: 22px; font-weight: 700; margin: 0; }
.ac-head .sub { color: #6b7280; font-size: 13px; margin: 4px 0 12px; }
.refresh { background: #2563eb; color: #fff; border: 0; padding: 7px 14px; border-radius: 8px; cursor: pointer; font-size: 13px; }
.refresh:disabled { opacity: .6; }
.loading { padding: 40px; text-align: center; color: #6b7280; }
/* spinner + loading overlays */
.spinner { display: inline-block; width: 14px; height: 14px; border: 2px solid #dbeafe; border-top-color: #2563eb; border-radius: 50%; animation: spin .7s linear infinite; vertical-align: -2px; }
.spinner.big { width: 22px; height: 22px; border-width: 3px; }
@keyframes spin { to { transform: rotate(360deg); } }
.inline-loader { color: #2563eb; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; }
.table-wrap { position: relative; min-height: 60px; }
.table-overlay { position: absolute; inset: 0; background: rgba(255,255,255,.72); backdrop-filter: blur(1.5px); display: flex; align-items: center; justify-content: center; gap: 8px; z-index: 10; color: #2563eb; font-size: 14px; font-weight: 500; border-radius: 14px; }
.denied { padding: 60px; text-align: center; color: #b45309; font-size: 16px; font-weight: 600; background: #fffbeb; border: 1px solid #fde68a; border-radius: 12px; margin-top: 20px; }
.realbox { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 14px; padding: 18px; margin: 14px 0; }
.realbox.muted-box { background: #f9fafb; color: #6b7280; text-align: center; }
.realhead h2 { font-size: 18px; font-weight: 700; margin: 0; }
.cols { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-top: 14px; }
.cols h3 { font-size: 14px; font-weight: 700; margin: 0 0 6px; }
.divide { border: 0; border-top: 1px dashed #d1d5db; margin: 28px 0 16px; }
.shadow-title { font-size: 16px; font-weight: 700; }
.collapsible { cursor: pointer; user-select: none; }
.collapsible .caret { display: inline-block; width: 14px; color: #6b7280; font-size: 12px; }
.collapsible:hover { color: #2563eb; }
@media (max-width: 820px) { .cols { grid-template-columns: 1fr; } }
.cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; margin: 14px 0; }
.card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px; box-shadow: 0 1px 3px rgba(0,0,0,.05); }
.card.warn { border-color: #fecaca; }
.card .big { font-size: 28px; font-weight: 800; color: #16a34a; }
.card.warn .big { color: #dc2626; }
.card .lbl { font-size: 13px; font-weight: 600; margin-top: 2px; }
.card .note { font-size: 11px; color: #9ca3af; margin-top: 2px; }
.verdict { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 10px 14px; font-size: 13px; }
.section { margin-top: 24px; }
.section h2 { font-size: 16px; font-weight: 700; }
.section .sub { color: #6b7280; font-size: 12px; margin: 2px 0 8px; }
table { width: 100%; border-collapse: collapse; font-size: 13px; }
th { text-align: left; color: #6b7280; font-weight: 600; font-size: 11px; text-transform: uppercase; padding: 6px 8px; border-bottom: 2px solid #e5e7eb; }
td { padding: 8px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
.thr td.good { color: #16a34a; font-weight: 700; }
.thr td.mid { color: #f59e0b; font-weight: 700; }
.thr td.low { color: #dc2626; }

/* ═══════════ Order table — polished design ═══════════ */
.rows-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(16,24,40,.04), 0 4px 16px -8px rgba(16,24,40,.10);
  overflow: hidden;            /* clip header corners + horizontal scroll */
  overflow-x: auto;
}
table.rows { font-size: 13px; min-width: 900px; }

/* header — sticky, soft gradient, clear separation */
table.rows thead th {
  position: sticky; top: 0; z-index: 2;
  background: linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%);
  color: #6b7280;
  font-size: 10.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
  padding: 11px 14px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

/* body cells — roomier, aligned, subtle dividers */
table.rows tbody td {
  padding: 12px 14px;
  border-bottom: 1px solid #f1f3f5;
  vertical-align: top;
  color: #1f2937;
}
table.rows tbody tr:last-child td { border-bottom: 0; }

/* zebra + hover */
table.rows tbody tr:nth-child(even) { background: #fcfcfd; }
table.rows tbody tr { transition: background .12s ease; }
table.rows tbody tr:hover { background: #f5f8ff; }

/* ── per-column treatment ── */
.c-date   { width: 118px; white-space: nowrap; }
.c-order  { width: 210px; }
.c-phone  { width: 140px; white-space: nowrap; }
.c-ai     { width: 92px; }
.c-conf   { width: 92px; }
.c-why    { min-width: 300px; }
.c-status { width: 130px; }
.c-verdict{ width: 96px; }

.cell-date { color: #6b7280; font-size: 12px; font-variant-numeric: tabular-nums; }
.order-ref { font-weight: 600; color: #111827; }
.cust-name { margin-top: 2px; color: #9ca3af; }
.phone-num { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; color: #4b5563; letter-spacing: -.01em; }

/* confidence — a small badge, color-coded by strength */
.conf-val {
  display: inline-block; padding: 2px 7px; border-radius: 6px;
  font-weight: 700; font-size: 12.5px; font-variant-numeric: tabular-nums;
}
.conf-val.good    { background: #dcfce7; color: #15803d; }
.conf-val.mid     { background: #fef3c7; color: #b45309; }
.conf-val.low     { background: #fee2e2; color: #dc2626; }
.conf-val.neutral { background: #f3f4f6; color: #6b7280; }
.model-tag { display: block; margin-top: 3px; font-size: 10px; color: #b0b6bf; text-transform: uppercase; letter-spacing: .03em; }

/* why cell — tighter, readable evidence lines */
table.rows .why { max-width: 380px; }
table.rows .why .pos,
table.rows .why .neg { font-size: 11.5px; line-height: 1.45; display: flex; gap: 5px; }
table.rows .why .pos { color: #15803d; }
table.rows .why .neg { color: #dc2626; }
table.rows .why .pos + .neg { margin-top: 2px; }
.plain-reason { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 10.5px; color: #b0b6bf; }

.ship-line { margin-top: 3px; }
.filters { margin-bottom: 8px; }
.filters button { background: #f3f4f6; border: 0; padding: 5px 12px; border-radius: 20px; margin-right: 6px; cursor: pointer; font-size: 12px; }
.filters button.active { background: #2563eb; color: #fff; }
/* ── Extended filters (search / model / status / confidence) ── */
.xfilters { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
.xfilters .xsearch { flex: 1 1 240px; min-width: 200px; padding: 7px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 13px; }
.xfilters .xsearch:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.xfilters select { padding: 7px 10px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 13px; background: #fff; cursor: pointer; }
/* custom dropdown */
.dd { position: relative; display: inline-block; }
.dd-btn { display: flex; align-items: center; gap: 8px; min-width: 150px; justify-content: space-between; padding: 7px 12px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; font-size: 13px; color: #1f2937; cursor: pointer; transition: border-color .15s; }
.dd-btn:hover { border-color: #9ca3af; }
.dd.open .dd-btn { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.dd-caret { color: #9ca3af; font-size: 11px; transition: transform .15s; }
.dd.open .dd-caret { transform: rotate(180deg); }
.dd-menu { position: absolute; top: calc(100% + 4px); left: 0; min-width: 100%; background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; box-shadow: 0 8px 24px -6px rgba(0,0,0,.18); padding: 4px; z-index: 50; max-height: 260px; overflow-y: auto; }
.dd-item { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: 7px; font-size: 13px; color: #374151; cursor: pointer; white-space: nowrap; }
.dd-item:hover { background: #f3f4f6; }
.dd-item.sel { background: #eff6ff; color: #2563eb; font-weight: 600; }
.dd-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 8px; }
/* confidence range — styled pill */
.conf-range { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #6b7280; }
.conf-range .clbl { font-weight: 700; color: #9ca3af; text-transform: uppercase; font-size: 10px; letter-spacing: .04em; }
.conf-inputs { display: flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #d1d5db; border-radius: 8px; padding: 4px 8px; }
.conf-inputs:focus-within { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.conf-inputs .cnum { width: 42px; padding: 3px 4px; border: 0; font-size: 13px; text-align: center; color: #1f2937; -moz-appearance: textfield; }
.conf-inputs .cnum:focus { outline: none; }
.conf-inputs .cnum::-webkit-outer-spin-button, .conf-inputs .cnum::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.conf-inputs .conf-sep { color: #9ca3af; font-size: 11px; }
.conf-inputs .conf-pct { color: #6b7280; font-weight: 600; }
.xfilters .xreset { background: #fff; border: 1px solid #e5e7eb; color: #6b7280; padding: 7px 12px; border-radius: 8px; cursor: pointer; font-size: 12px; }
.xfilters .xreset:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
/* ═══ Unified filter card ═══ */
.filter-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 14px 16px; margin-bottom: 14px; box-shadow: 0 1px 3px rgba(0,0,0,.05); }
.filter-card .frow { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-bottom: 1px dashed #f3f4f6; }
.filter-card .frow:last-of-type { border-bottom: 0; }
.filter-card .frow.wrap { flex-wrap: wrap; }
.filter-card .frow-label { font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .04em; width: 70px; flex: 0 0 70px; }
.filter-card .pills { display: flex; flex-wrap: wrap; gap: 6px; }
.filter-card .pills button { background: #f3f4f6; border: 1px solid #e5e7eb; color: #374151; padding: 5px 12px; border-radius: 20px; cursor: pointer; font-size: 12px; }
.filter-card .pills button.active { background: #2563eb; border-color: #2563eb; color: #fff; }
.fcard-foot { display: flex; align-items: center; gap: 12px; padding-top: 10px; margin-top: 4px; border-top: 1px solid #f3f4f6; flex-wrap: wrap; }
.fcard-foot .date-active { background: #eff6ff; color: #2563eb; border-radius: 8px; padding: 3px 10px; font-size: 12px; }
.reset-all { margin-left: auto; background: #fff; border: 1px solid #e5e7eb; color: #6b7280; padding: 6px 12px; border-radius: 8px; cursor: pointer; font-size: 12px; }
.reset-all:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
.result-count { margin: 2px 0 6px; }
.count-line b { color: #1f2937; }
.count-line .dim { color: #9ca3af; }
.trunc-warn { margin-top: 8px; font-size: 12px; color: #b45309; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 7px 12px; }
/* colored real-status badge */
.status-badge { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.status-badge.st-green { background: #dcfce7; color: #16a34a; }
.status-badge.st-red { background: #fee2e2; color: #dc2626; }
.status-badge.st-blue { background: #dbeafe; color: #2563eb; }
.status-badge.st-gray { background: #f3f4f6; color: #6b7280; }
.ship-line { margin-top: 3px; }
.vpill { background: #f3f4f6; border: 1px solid #e5e7eb; color: #374151; padding: 5px 11px; border-radius: 20px; cursor: pointer; font-size: 12px; }
.vpill.v-right { color: #16a34a; }
.vpill.v-wrong { color: #dc2626; }
.vpill.v-pending { color: #9ca3af; }
.vpill.active { background: #1f2937; color: #fff; border-color: #1f2937; }
.vpill.v-right.active { background: #16a34a; border-color: #16a34a; color: #fff; }
.vpill.v-wrong.active { background: #dc2626; border-color: #dc2626; color: #fff; }

/* ── Polished date filter ── */
.date-bar { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px 14px; margin-bottom: 14px; box-shadow: 0 1px 2px rgba(0,0,0,.04); }
.quick-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.quick-chips button { background: #f3f4f6; border: 1px solid #e5e7eb; color: #374151; padding: 5px 12px; border-radius: 20px; cursor: pointer; font-size: 12px; font-weight: 500; transition: all .15s; }
.quick-chips button:hover { background: #e5e7eb; }
.quick-chips button.on { background: #2563eb; border-color: #2563eb; color: #fff; }
.date-fields { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.date-field { display: flex; flex-direction: column; gap: 2px; }
.date-field .dlabel { font-size: 10px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .04em; }
.date-field input[type="date"] { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 13px; color: #1f2937; background: #fff; cursor: pointer; font-family: inherit; transition: border-color .15s; }
.date-field input[type="date"]:hover { border-color: #9ca3af; }
.date-field input[type="date"]:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
/* style the native calendar icon blue + bigger */
.date-field input[type="date"]::-webkit-calendar-picker-indicator {
  cursor: pointer; opacity: .6; filter: invert(35%) sepia(90%) saturate(1500%) hue-rotate(205deg);
  transition: opacity .15s;
}
.date-field input[type="date"]::-webkit-calendar-picker-indicator:hover { opacity: 1; }
.date-fields .arrow { color: #9ca3af; font-size: 16px; margin-top: 14px; }
/* custom-theme the vue2-datepicker to match the page */
.ac-datepicker { width: 260px; }
.ac-datepicker >>> .mx-input { height: 38px; border: 1px solid #d1d5db; border-radius: 8px; box-shadow: none; font-size: 13px; color: #1f2937; padding-left: 12px; }
.ac-datepicker >>> .mx-input:hover { border-color: #9ca3af; }
.ac-datepicker >>> .mx-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.12); }
.ac-datepicker >>> .mx-icon-calendar, .ac-datepicker >>> .mx-icon-clear { color: #2563eb; }
.btn-apply { background: #2563eb; color: #fff; border: 0; padding: 8px 18px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; margin-top: 14px; }
.btn-apply:hover { background: #1d4ed8; }
.btn-apply:disabled { opacity: .6; cursor: default; }
.btn-clear { background: #fff; border: 1px solid #e5e7eb; color: #6b7280; padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px; margin-top: 14px; }
.btn-clear:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
.date-active { margin-top: 10px; font-size: 12px; color: #6b7280; background: #eff6ff; border-radius: 8px; padding: 6px 10px; display: inline-block; }
.date-active b { color: #2563eb; }
.pill { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 600; }
.pill.ok { background: #dcfce7; color: #16a34a; }
.pill.warn { background: #fef3c7; color: #b45309; }
.pill.bad { background: #fee2e2; color: #dc2626; }
.muted-pill { background: #f3f4f6; color: #9ca3af; }
.muted { color: #9ca3af; }
.sm { font-size: 11px; }
/* base .why kept as fallback; the polished rules live under table.rows above */
.why { max-width: 320px; }
/* ── Polished pagination bar ── */
.empty-rows { text-align: center; color: #9ca3af; padding: 28px; font-size: 14px; }
.pagination { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 14px; padding-top: 12px; border-top: 1px solid #f3f4f6; flex-wrap: wrap; }
.pagination .pg-info { font-size: 13px; color: #4b5563; }
.pagination .pg-info b { color: #1f2937; }
.pagination .pg-controls { display: flex; align-items: center; gap: 4px; }
.pg-btn, .pg-num { min-width: 32px; height: 32px; padding: 0 8px; border: 1px solid #e5e7eb; background: #fff; border-radius: 8px; cursor: pointer; font-size: 13px; color: #374151; display: inline-flex; align-items: center; justify-content: center; transition: all .12s; }
.pg-btn:hover:not(:disabled), .pg-num:hover { background: #f3f4f6; border-color: #d1d5db; }
.pg-btn:disabled { opacity: .4; cursor: default; }
.pg-num.on { background: #2563eb; border-color: #2563eb; color: #fff; font-weight: 600; }
.pagination .pg-size { display: flex; align-items: center; gap: 4px; }
.pg-size-btn { min-width: 30px; height: 28px; border: 1px solid #e5e7eb; background: #fff; border-radius: 6px; cursor: pointer; font-size: 12px; color: #6b7280; }
.pg-size-btn:hover { background: #f3f4f6; }
.pg-size-btn.on { background: #eff6ff; border-color: #2563eb; color: #2563eb; font-weight: 600; }
@media (max-width: 700px) { .pagination { justify-content: center; } }
</style>

<!-- non-scoped: the datepicker popup renders at <body> root, so it can't be scoped -->
<style>
.mx-datepicker-main { border-radius: 12px !important; box-shadow: 0 12px 32px -6px rgba(0,0,0,.2) !important; border: 1px solid #e5e7eb !important; font-family: inherit; }
.mx-datepicker-main .mx-calendar-content .cell.active { background: #2563eb !important; border-radius: 8px; }
.mx-datepicker-main .mx-calendar-content .cell.in-range { background: #eff6ff !important; color: #2563eb !important; }
.mx-datepicker-main .mx-calendar-content .cell.hover-in-range { background: #dbeafe !important; }
.mx-datepicker-main .mx-calendar-content .cell:not(.disabled):hover { background: #dbeafe !important; border-radius: 8px; }
.mx-datepicker-main .mx-btn:hover { color: #2563eb !important; border-color: #2563eb !important; }
.mx-datepicker-main .mx-table-date td.today { color: #2563eb !important; font-weight: 700; }
</style>
